import clsx from 'clsx';
import { ErrorCatcher } from '../../../tools/error-catcher';

type CheckboxProps = {
    whenClicked: (e?: any) => void;
    value: string;
    isChecked: boolean;
    name: string;
    // Sorry, name was taken.
    inputName?: string;
    hasErrors?: boolean;
    id?: string;
    inputRef?: (el: HTMLInputElement) => void;
    title?: string;
    'aria-label'?: string;
    'aria-describedby'?: string;
};

/**
 * The native `<input>` is `appearance-none`; the visible box and checkmark are drawn by sibling
 * spans selected via the `peer` / `peer-checked` cascade. Checkmark stroke is `currentColor` so
 * it inherits from the surrounding text-color utility (`text-cvi-fg-on-color` = white here).
 */
export const Checkbox = (props: CheckboxProps) => {
    const inputId = () => props.id || props.value;

    return (
        <ErrorCatcher componentName='Checkbox'>
            <label
                for={inputId()}
                onInput={props.whenClicked}
                class="inline-flex items-center gap-cvi-5 py-cvi-4 cursor-pointer"
            >
                <span class="relative inline-flex shrink-0 size-cvi-8 text-cvi-fg-on-color">
                    <input
                        id={inputId()}
                        type='checkbox'
                        value={props.value}
                        name={props.inputName}
                        ref={props.inputRef}
                        title={props.title}
                        checked={props.isChecked}
                        aria-label={props['aria-label']}
                        aria-describedby={props['aria-describedby']}
                        class={clsx(
                            'peer absolute inset-0 m-0 appearance-none cursor-pointer rounded-cvi-sm border bg-cvi-bg-field',
                            'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-offset-2 focus-visible:ring-offset-cvi-bg-body',
                            props.hasErrors
                                ? 'border-cvi-border-danger checked:bg-cvi-bg-danger focus-visible:ring-cvi-focus-danger'
                                : 'border-cvi-border-strong checked:bg-cvi-bg-interactive checked:border-cvi-border-interactive focus-visible:ring-cvi-focus-default focus-visible:border-cvi-border-interactive',
                        )}
                    />
                    {/* Checkmark — shown only when peer (`<input>`) is checked. SVG inherits color via currentColor.
                        Path bounds + stroke width tuned to match Figma 156:4808 (`shape` inset-[17.86%_7.14%]). */}
                    <svg
                        class="absolute inset-0 size-full hidden peer-checked:block pointer-events-none"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                    >
                        <path d="M6 12.5L10 16.5L18 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </span>
                {props.name && (
                    <span class="label-md font-cvi-regular text-cvi-fg-default">
                        {props.name}
                    </span>
                )}
            </label>
        </ErrorCatcher>
    );
};
